import { interiorModules } from "../data/catalog";
import { interiorLayoutMasters } from "../data/expansion";
import {
  getMarketplaceGuide,
  recommendedLayoutIds,
  recommendedModuleIds,
} from "../data/marketplaces";
import type { PaperKingdomProject } from "../domain/schema";

export type PageRole =
  | "opening"
  | "setup"
  | "section"
  | "monthly"
  | "weekly"
  | "seasonal"
  | "challenge"
  | "support"
  | "primary"
  | "closing";

export interface AdaptivePageBlueprintEntry {
  key: string;
  name: string;
  purpose: string;
  structure: string;
  role: PageRole;
  count: number;
}

export interface AdaptivePageBlueprint {
  mode: "bound-print" | "reusable-digital";
  targetPageCount: number;
  entries: AdaptivePageBlueprintEntry[];
  sequence: string[];
  countSummary: string;
  sequenceSummary: string;
  manifest: string;
}

function textSignature(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `adaptive-v1-${(hash >>> 0).toString(36)}`;
}

const digitalPageTargets: Record<PaperKingdomProject["format"], number> = {
  "Low-content paperback": 24,
  "Guided journal": 24,
  "Activity book": 30,
  Workbook: 24,
  Planner: 18,
  "Printable PDF": 12,
};

function normalize(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function classifyPage(id: string, name: string): PageRole {
  const value = normalize(`${id} ${name}`);
  if (value.includes("orientation")) return "opening";
  if (value.includes("section opener")) return "section";
  if (
    value.includes("closing") ||
    value.includes("completion review") ||
    value.includes("next volume") ||
    value.includes("annual index")
  ) return "closing";
  if (value.includes("monthly")) return "monthly";
  if (value.includes("weekly")) return "weekly";
  if (value.includes("seasonal")) return "seasonal";
  if (value.includes("challenge")) return "challenge";
  if (
    value.includes("inventory") ||
    value.includes("values sort") ||
    value.includes("identity statement")
  ) return "setup";
  if (
    value.includes("tracker") ||
    value.includes("evidence") ||
    value.includes("resource") ||
    value.includes("reference") ||
    value.includes("conversation") ||
    value.includes("appointment") ||
    value.includes("support network") ||
    value.includes("log") ||
    value.includes("brief")
  ) return "support";
  return "primary";
}

function selectedEntries(
  project: PaperKingdomProject,
  bookId: string,
): AdaptivePageBlueprintEntry[] {
  const plan = project.production.interiorPlans[bookId];
  const layoutIds = plan?.layoutIds.length
    ? plan.layoutIds
    : recommendedLayoutIds(project.format);
  const moduleIds = plan?.moduleIds.length
    ? plan.moduleIds
    : recommendedModuleIds(project.format);
  const rawEntries: AdaptivePageBlueprintEntry[] = [
    ...layoutIds.flatMap((id) => {
      const item = interiorLayoutMasters.find((candidate) => candidate.id === id);
      return item
        ? [{
            key: `layout:${id}`,
            name: item.name,
            purpose: item.purpose,
            structure: item.structure,
            role: classifyPage(id, item.name),
            count: 1,
          }]
        : [];
    }),
    ...moduleIds.flatMap((id) => {
      const item = interiorModules.find((candidate) => candidate.id === id);
      return item
        ? [{
            key: `module:${id}`,
            name: item.name,
            purpose: item.purpose,
            structure: item.pageSystem,
            role: classifyPage(id, item.name),
            count: 1,
          }]
        : [];
    }),
  ];

  const seenFunctions = new Set<string>();
  return rawEntries.filter((entry) => {
    const key = normalize(entry.name);
    if (seenFunctions.has(key)) return false;
    seenFunctions.add(key);
    return true;
  });
}

function desiredCount(
  entry: AdaptivePageBlueprintEntry,
  project: PaperKingdomProject,
  mode: AdaptivePageBlueprint["mode"],
): number {
  if (entry.role === "opening" || entry.role === "closing") return 1;
  if (mode === "reusable-digital") {
    if (entry.role === "section") return 4;
    if (entry.role === "seasonal") return 4;
    if (entry.role === "challenge") return 7;
    if (entry.role === "setup" || entry.role === "support") return 2;
    return 1;
  }

  if (entry.role === "section") {
    return ["Workbook", "Activity book"].includes(project.format) ? 6 : 4;
  }
  if (entry.role === "monthly") return 12;
  if (entry.role === "weekly") return project.format === "Planner" ? 52 : 12;
  if (entry.role === "seasonal") return 4;
  if (entry.role === "challenge") return 7;
  if (entry.role === "setup") {
    return ["Planner", "Low-content paperback"].includes(project.format) ? 4 : 2;
  }
  if (entry.role === "support") {
    const value = normalize(entry.name);
    if (value.includes("conversation") || value.includes("appointment")) return 12;
    if (value.includes("tracker") || value.includes("evidence") || value.includes("log")) return 8;
    return 4;
  }
  return 1;
}

function allocationPriority(role: PageRole): number {
  return {
    opening: 0,
    closing: 0,
    setup: 1,
    monthly: 2,
    weekly: 2,
    seasonal: 2,
    section: 3,
    challenge: 3,
    support: 4,
    primary: 5,
  }[role];
}

function addDesiredCounts(
  entries: AdaptivePageBlueprintEntry[],
  targetPageCount: number,
  project: PaperKingdomProject,
  mode: AdaptivePageBlueprint["mode"],
): number {
  let remaining = Math.max(0, targetPageCount - entries.length);
  for (const entry of [...entries].sort(
    (a, b) => allocationPriority(a.role) - allocationPriority(b.role),
  )) {
    const extra = Math.min(
      remaining,
      Math.max(0, desiredCount(entry, project, mode) - entry.count),
    );
    entry.count += extra;
    remaining -= extra;
  }
  return remaining;
}

function fillRemainingPages(
  entries: AdaptivePageBlueprintEntry[],
  remaining: number,
): void {
  const primary = entries.filter((entry) => entry.role === "primary");
  const working = entries.filter((entry) => !["opening", "closing", "setup", "monthly"].includes(entry.role));
  const fallback = entries.filter((entry) => !["opening", "closing"].includes(entry.role));
  const candidates = primary.length ? primary : working.length ? working : fallback.length ? fallback : entries;
  for (let index = 0; index < remaining; index += 1) {
    candidates[index % candidates.length].count += 1;
  }
}

function balancedNames(entries: AdaptivePageBlueprintEntry[]): string[] {
  const remaining = new Map(entries.map((entry) => [entry.key, entry.count]));
  const result: string[] = [];
  while ([...remaining.values()].some((count) => count > 0)) {
    for (const entry of entries) {
      const count = remaining.get(entry.key) ?? 0;
      if (count > 0) {
        result.push(entry.name);
        remaining.set(entry.key, count - 1);
      }
    }
  }
  return result;
}

function distributeAcrossCycles(items: string[], cycleCount: number): string[][] {
  const cycles = Array.from({ length: cycleCount }, () => [] as string[]);
  const baseSize = Math.floor(items.length / cycleCount);
  const remainder = items.length % cycleCount;
  let cursor = 0;
  cycles.forEach((cycle, index) => {
    const size = baseSize + (index < remainder ? 1 : 0);
    cycle.push(...items.slice(cursor, cursor + size));
    cursor += size;
  });
  return cycles;
}

function buildSequence(entries: AdaptivePageBlueprintEntry[]): string[] {
  const opening = balancedNames(entries.filter((entry) => entry.role === "opening"));
  const setup = balancedNames(entries.filter((entry) => entry.role === "setup"));
  const closing = balancedNames(entries.filter((entry) => entry.role === "closing"));
  const monthly = entries.filter((entry) => entry.role === "monthly");
  const weekly = entries.filter((entry) => entry.role === "weekly");
  const sections = entries.filter((entry) => entry.role === "section");

  const bodyEntries = entries.filter(
    (entry) => !["opening", "setup", "closing", "monthly", "weekly", "section"].includes(entry.role),
  );
  const body = balancedNames(bodyEntries);

  if (monthly.length) {
    const monthlyNames = balancedNames(monthly);
    const supporting = balancedNames([...weekly, ...sections, ...bodyEntries]);
    const cycles = distributeAcrossCycles(supporting, monthlyNames.length);
    return [
      ...opening,
      ...setup,
      ...monthlyNames.flatMap((name, index) => [name, ...cycles[index]]),
      ...closing,
    ];
  }

  if (weekly.length) {
    const weeklyNames = balancedNames(weekly);
    const supporting = balancedNames([...sections, ...bodyEntries]);
    const cycles = distributeAcrossCycles(supporting, weeklyNames.length);
    return [
      ...opening,
      ...setup,
      ...weeklyNames.flatMap((name, index) => [...cycles[index], name]),
      ...closing,
    ];
  }

  if (sections.length) {
    const sectionNames = balancedNames(sections);
    const cycles = distributeAcrossCycles(body, sectionNames.length);
    return [
      ...opening,
      ...setup,
      ...sectionNames.flatMap((name, index) => [name, ...cycles[index]]),
      ...closing,
    ];
  }

  return [...opening, ...setup, ...body, ...closing];
}

function buildManifest(sequence: string[]): string {
  const lines: string[] = [];
  let start = 0;
  for (let index = 1; index <= sequence.length; index += 1) {
    if (index < sequence.length && sequence[index] === sequence[start]) continue;
    const firstPage = start + 1;
    const lastPage = index;
    lines.push(
      firstPage === lastPage
        ? `Page ${firstPage}: ${sequence[start]}`
        : `Pages ${firstPage}-${lastPage}: ${sequence[start]} (${lastPage - firstPage + 1} pages)`,
    );
    start = index;
  }
  return lines.join("\n");
}

function buildSequenceSummary(entries: AdaptivePageBlueprintEntry[]): string {
  const opening = entries.filter((entry) => entry.role === "opening");
  const setup = entries.filter((entry) => entry.role === "setup");
  const monthly = entries.filter((entry) => entry.role === "monthly");
  const weekly = entries.filter((entry) => entry.role === "weekly");
  const section = entries.filter((entry) => entry.role === "section");
  const closing = entries.filter((entry) => entry.role === "closing");
  const working = entries.filter(
    (entry) => !["opening", "setup", "monthly", "weekly", "section", "closing"].includes(entry.role),
  );
  const parts: string[] = [];
  if (opening.length) parts.push(`begin with ${opening.map((entry) => entry.name).join(" and ")}`);
  if (setup.length) parts.push(`complete ${setup.map((entry) => `${entry.count} ${entry.name} page${entry.count === 1 ? "" : "s"}`).join(" and ")}`);
  if (monthly.length) {
    parts.push(`use ${monthly.reduce((sum, entry) => sum + entry.count, 0)} monthly sections, each beginning with ${monthly.map((entry) => entry.name).join(" and ")}, with the working pages distributed through those sections`);
  } else if (weekly.length) {
    parts.push(`group the working pages into ${weekly.reduce((sum, entry) => sum + entry.count, 0)} cycles ending with ${weekly.map((entry) => entry.name).join(" and ")}`);
  } else if (section.length) {
    parts.push(`divide the body into ${section.reduce((sum, entry) => sum + entry.count, 0)} sections`);
  } else if (working.length) {
    parts.push(`continue with the working pages in the manifest order`);
  }
  if (closing.length) parts.push(`finish with ${closing.map((entry) => entry.name).join(" and ")}`);
  return `${parts.join(", then ")}.`;
}

export function buildAdaptivePageBlueprint(
  project: PaperKingdomProject,
  bookId: string,
): AdaptivePageBlueprint {
  const usesBoundPrint =
    getMarketplaceGuide(project.marketplace).usesKdpPrintSetup ||
    (project.marketplace === "Shopify / direct" && project.format === "Low-content paperback");
  const mode: AdaptivePageBlueprint["mode"] = usesBoundPrint
    ? "bound-print"
    : "reusable-digital";
  const entries = selectedEntries(project, bookId);
  const targetPageCount = mode === "bound-print"
    ? project.production.plannedPageCount
    : Math.max(digitalPageTargets[project.format], entries.length);

  if (!entries.length) {
    return {
      mode,
      targetPageCount,
      entries: [],
      sequence: [],
      countSummary: "No page types selected",
      sequenceSummary: "Choose the inside pages before creating the file.",
      manifest: "No page manifest is available.",
    };
  }

  const remaining = addDesiredCounts(entries, targetPageCount, project, mode);
  fillRemainingPages(entries, remaining);
  const sequence = buildSequence(entries);
  const countSummary = entries
    .map((entry) => `${entry.count} × ${entry.name}`)
    .join("; ");

  return {
    mode,
    targetPageCount,
    entries,
    sequence,
    countSummary,
    sequenceSummary: buildSequenceSummary(entries),
    manifest: buildManifest(sequence),
  };
}

export function getPrimaryFileBlueprintSignature(
  project: PaperKingdomProject,
  bookId: string,
): string {
  const book = project.catalogue.find((item) => item.id === bookId) ?? project.catalogue[0];
  const blueprint = buildAdaptivePageBlueprint(project, bookId);
  const interiorWork = Object.fromEntries(
    Object.entries(project.promptWork).filter(([key]) => key.includes("pk-interior-01")),
  );
  return textSignature(JSON.stringify({
    ...(getMarketplaceGuide(project.marketplace).usesKdpPrintSetup
      ? {}
      : { customerFilePromptVersion: 3 }),
    marketplace: project.marketplace,
    format: project.format,
    audience: project.audience,
    theme: project.theme,
    notes: project.notes,
    book,
    interiorPlan: project.production.interiorPlans[bookId],
    interiorWork,
    coverSystemId: project.production.coverSystemId,
    coverPaletteId: project.production.coverPaletteId,
    customCoverPalette: project.production.customCoverPalette,
    trimSize: project.production.trimSize,
    paperType: project.production.paperType,
    bleed: project.production.bleed,
    typographySpecification: project.production.typographySpecification,
    interiorRules: project.imprint.interiorRules,
    voiceRules: project.imprint.voiceRules,
    revisionNotes: project.production.revisionNotes,
    countSummary: blueprint.countSummary,
    manifest: blueprint.manifest,
  }));
}

export function getCoverFileBlueprintSignature(
  project: PaperKingdomProject,
  bookId: string,
): string {
  const book = project.catalogue.find((item) => item.id === bookId) ?? project.catalogue[0];
  return textSignature(JSON.stringify({
    coverFilePromptVersion: 5,
    primary: getPrimaryFileBlueprintSignature(project, bookId),
    book,
    coverPlan: project.production.coverPlans[bookId],
    coverSystemId: project.production.coverSystemId,
    coverPaletteId: project.production.coverPaletteId,
    customCoverPalette: project.production.customCoverPalette,
    coverHierarchy: project.production.coverHierarchy,
    backCoverFramework: project.production.backCoverFramework,
    coverNotes: project.production.coverNotes,
    trimSize: project.production.trimSize,
    paperType: project.production.paperType,
    plannedPageCount: project.production.plannedPageCount,
    barcodeTreatment: project.production.barcodeTreatment,
    imprintName: project.imprint.name,
    imprintTypographyMood: project.imprint.typographyMood,
    imprintCoverRules: project.imprint.coverRules,
  }));
}
